const DEBOUNCE_MS = 200;
const HITS_PER_PAGE = 1000;

class FaqSearch extends HTMLElement {
  #debounce = 0;
  #terms = '';

  connectedCallback() {
    this.root = this.closest('.faq-topics');
    this.input = this.querySelector('.faq-topics__search-input');
    this.noResults = this.querySelector('.faq-topics__no-results');

    if (!this.root || !this.input) return;

    // Build the Algolia client from the site-wide config. If it isn't available,
    // hide the box so the page still works without JS-driven search.
    const config = window.algoliaShopify?.config;
    const algoliasearch = window.algoliaShopify?.externals?.algoliasearch;
    if (!config || typeof algoliasearch !== 'function') {
      this.hidden = true;
      return;
    }
    this.client = algoliasearch(config.app_id, config.search_api_key || config.api_key);
    this.indexName = this.#resolveIndexPrefix() + 'articles';
    this.languageCode = this.dataset.languageCode
      || (window.Shopify?.locale?.startsWith('fr') ? 'fr' : 'en');

    // Collect the grids we filter, caching their original markup for restore.
    // Featured grid matches on the featured tag; topic grids match on blog handle.
    this.grids = [];

    const featured = this.root.querySelector('.faq-topics__featured[data-featured-tag]');
    if (featured) {
      const list = featured.querySelector('.faq-topics__featured-list');
      if (list) {
        this.grids.push({
          wrapper: featured,
          list,
          featuredTag: featured.dataset.featuredTag,
          originalHtml: list.innerHTML,
        });
      }
    }

    this.root.querySelectorAll('.faq-topics__category[data-blog-handle]').forEach((category) => {
      const list = category.querySelector('.faq-topics__category-list');
      if (!list) return;
      this.grids.push({
        wrapper: category,
        list,
        blogHandle: category.dataset.blogHandle,
        pagination: category.querySelector('.faq-topics__pagination'),
        originalHtml: list.innerHTML,
      });
    });

    // A single accordion row we can clone so results render identically to the
    // server-rendered rows (same markup, +/- icon, open-state styling).
    const templateRow = this.root.querySelector('.faq-topics__item');
    this.templateRow = templateRow ? templateRow.cloneNode(true) : null;

    this.input.addEventListener('input', this.#handleInput);
    this.input.addEventListener('keydown', this.#handleKeydown);
    this.querySelector('.faq-topics__search-form')?.addEventListener('submit', (event) =>
      event.preventDefault()
    );
  }

  disconnectedCallback() {
    clearTimeout(this.#debounce);
    this.input?.removeEventListener('input', this.#handleInput);
    this.input?.removeEventListener('keydown', this.#handleKeydown);
  }

  // Mirrors resolveIndexPrefix() in algolia-plp-hooks.js: theme setting wins,
  // else app config; always normalised to a trailing "_".
  #resolveIndexPrefix() {
    const themeP = window.themeSettings?.algoliaIndexPrefix;
    const appP = window.algoliaShopify?.config?.index_prefix || '';
    const raw = themeP && themeP.trim() ? themeP.trim() : appP;
    return raw && !raw.endsWith('_') ? raw + '_' : raw;
  }

  #handleInput = () => {
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.#run(this.input.value.trim()), DEBOUNCE_MS);
  };

  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.input.value) {
      event.preventDefault();
      clearTimeout(this.#debounce);
      this.input.value = '';
      this.#restore();
    }
  };

  async #run(terms) {
    if (!terms) {
      this.#restore();
      return;
    }

    this.#terms = terms;

    // Single query scoped to the locale; `blog.handle` is not facetable so we
    // partition the hits client-side below.
    let hits = [];
    try {
      const res = await this.client.initIndex(this.indexName).search(terms, {
        filters: `tags:language--${this.languageCode}`,
        hitsPerPage: HITS_PER_PAGE,
        distinct: true,
        // Treat the query as a word prefix so a single typed letter matches
        // (e.g. "r" -> "refund"). Production's index does this by default; the
        // staging index's default is stricter, so we set it explicitly here.
        queryType: 'prefixLast',
      });
      hits = res.hits || [];
    } catch (error) {
      console.warn('[faq-search]', error);
      hits = [];
    }

    let total = 0;
    this.grids.forEach((grid) => {
      const gridHits = grid.featuredTag
        ? hits.filter((hit) => Array.isArray(hit.tags) && hit.tags.includes(grid.featuredTag))
        : hits.filter((hit) => hit.blog?.handle === grid.blogHandle);

      total += gridHits.length;
      this.#renderGrid(grid, gridHits);
      grid.wrapper.hidden = gridHits.length === 0;
      // Load More is server-pagination for the un-searched view; hide it while searching.
      if (grid.pagination) grid.pagination.hidden = true;
    });

    if (this.noResults) {
      this.noResults.hidden = total > 0;
      if (total === 0) {
        this.noResults.textContent =
          this.languageCode === 'fr' ? 'Aucun résultat trouvé.' : 'No results found.';
      }
    }
  }

  #renderGrid(grid, hits) {
    grid.list.innerHTML = '';
    if (!this.templateRow) return;
    const fragment = document.createDocumentFragment();
    hits.forEach((hit) => {
      const row = this.#buildRow(hit);
      if (row) fragment.appendChild(row);
    });
    grid.list.appendChild(fragment);
  }

  #buildRow(hit) {
    if (!this.templateRow) return null;
    const node = this.templateRow.cloneNode(true);

    const details = node.querySelector('.details');
    if (details) details.removeAttribute('open');

    const summary = node.querySelector('.details__header');
    if (summary) {
      // Replace the leading title while keeping the trailing icon span. Wrap the
      // typed letters in <strong> ourselves so they are always highlighted,
      // regardless of which attribute Algolia matched on.
      const wrapper = document.createElement('span');
      wrapper.className = 'faq-topics__result-title';
      wrapper.innerHTML = this.#highlight(hit.title || '', this.#terms);
      [...summary.childNodes].forEach((child) => {
        if (child.nodeType === Node.TEXT_NODE) child.remove();
      });
      summary.insertBefore(wrapper, summary.firstChild);
    }

    // Prefer body_html (keeps <p> markup, so section CSS applies) — mirrors
    // Production's FAQSearch, which renders `q.body_html`. Fall back to the
    // sanitized field if body_html isn't present on the record.
    const content = node.querySelector('.details-content');
    if (content) content.innerHTML = hit.body_html || hit.body_html_safe || '';

    return node;
  }

  // Escape plain text so it renders safely as HTML.
  #escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Wrap each occurrence of the typed terms in <strong>, returning escaped HTML.
  // Case-insensitive; each whitespace-separated term is matched independently so
  // a single letter highlights wherever it appears in the title.
  #highlight(title, terms) {
    if (!terms) return this.#escape(title);
    const parts = terms
      .split(/\s+/)
      .filter(Boolean)
      .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!parts.length) return this.#escape(title);

    const re = new RegExp(`(${parts.join('|')})`, 'gi');
    let out = '';
    let last = 0;
    let match;
    while ((match = re.exec(title)) !== null) {
      out += this.#escape(title.slice(last, match.index));
      out += `<strong>${this.#escape(match[0])}</strong>`;
      last = match.index + match[0].length;
      // Guard against zero-length matches looping forever.
      if (match.index === re.lastIndex) re.lastIndex++;
    }
    out += this.#escape(title.slice(last));
    return out;
  }

  #restore() {
    this.grids.forEach((grid) => {
      grid.list.innerHTML = grid.originalHtml;
      grid.wrapper.hidden = false;
      if (grid.pagination) grid.pagination.hidden = false;
    });
    if (this.noResults) this.noResults.hidden = true;
  }
}

if (!customElements.get('faq-search')) {
  customElements.define('faq-search', FaqSearch);
}
